// Admin Backup button — one-click JSON export of every business collection.
// Sits in the AdminDashboard header next to Refresh.
//
// Two actions:
//   1. Show preview popover with per-collection counts (cheap GET).
//   2. Click "Download" → streams the full JSON archive to a file save.
import { useState } from "react";
import axios from "axios";
import { toast } from "sonner";
import { Download, Database, Loader2 } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle } from "@/components/ui/dialog";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function BackupButton() {
  const [open, setOpen] = useState(false);
  const [preview, setPreview] = useState(null);
  const [loading, setLoading] = useState(false);
  const [downloading, setDownloading] = useState(false);

  const onOpen = async () => {
    setOpen(true);
    setLoading(true);
    setPreview(null);
    try {
      const r = await axios.get(`${API}/admin/backup/preview`);
      setPreview(r.data);
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Could not load backup preview");
    } finally {
      setLoading(false);
    }
  };

  const onDownload = async () => {
    setDownloading(true);
    try {
      // Use axios responseType=blob so the browser saves bytes as-is.
      const r = await axios.get(`${API}/admin/backup`, { responseType: "blob" });
      // Pull a filename from Content-Disposition (server provides timestamped name).
      const cd = r.headers["content-disposition"] || "";
      const m = cd.match(/filename="?([^";]+)"?/i);
      const fname = m ? m[1] : `mdm-sahayak-backup-${Date.now()}.json`;
      const url = URL.createObjectURL(new Blob([r.data], { type: "application/json" }));
      const a = document.createElement("a");
      a.href = url;
      a.download = fname;
      document.body.appendChild(a);
      a.click();
      a.remove();
      URL.revokeObjectURL(url);
      toast.success(`Backup downloaded — ${fname}`);
      setOpen(false);
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Backup download failed");
    } finally {
      setDownloading(false);
    }
  };

  const entries = preview ? Object.entries(preview.counts || {}) : [];
  const total = preview?.totalDocuments || 0;

  return (
    <>
      <Button
        variant="outline"
        onClick={onOpen}
        data-testid="admin-backup-btn"
        className="gap-1.5"
      >
        <Database className="h-4 w-4" />
        Backup
      </Button>

      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent className="max-w-lg" data-testid="admin-backup-dialog">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2 text-2xl">
              <Database className="h-5 w-5 text-clay" /> Data Backup
            </DialogTitle>
            <DialogDescription>
              Download a single JSON archive of every business collection. Sensitive auth fields (password hashes) are redacted automatically.
            </DialogDescription>
          </DialogHeader>

          <div className="rounded-xl border border-border bg-sand-50 p-4" data-testid="admin-backup-preview">
            {loading && (
              <p className="flex items-center gap-2 text-sm text-ink-700">
                <Loader2 className="h-4 w-4 animate-spin" /> Counting documents…
              </p>
            )}
            {!loading && preview && (
              <>
                <p className="text-[0.7rem] font-semibold uppercase tracking-[0.16em] text-clay">Archive Preview</p>
                <p className="mt-1 text-2xl font-bold text-ink-900 font-mono" data-testid="admin-backup-total">
                  {total.toLocaleString()} documents
                </p>
                <div className="mt-3 grid max-h-64 grid-cols-1 gap-1 overflow-y-auto pr-1 sm:grid-cols-2">
                  {entries.map(([col, n]) => (
                    <div key={col} className="flex items-center justify-between rounded border border-border bg-white px-2 py-1.5 text-xs">
                      <span className="font-mono text-ink-700">{col}</span>
                      <b className="font-mono text-ink-900">{n}</b>
                    </div>
                  ))}
                </div>
                <p className="mt-3 text-[0.7rem] uppercase tracking-[0.14em] text-ink-700">
                  Format: <b>application/json</b> · UTF-8 · structured archive
                </p>
              </>
            )}
          </div>

          <DialogFooter className="gap-2">
            <Button variant="outline" onClick={() => setOpen(false)} disabled={downloading} data-testid="admin-backup-cancel">
              Cancel
            </Button>
            <Button
              onClick={onDownload}
              disabled={loading || downloading || !preview}
              data-testid="admin-backup-download"
              className="gap-1.5 bg-clay text-white hover:bg-clay-dark"
            >
              {downloading ? <Loader2 className="h-4 w-4 animate-spin" /> : <Download className="h-4 w-4" />}
              Download JSON
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </>
  );
}
